/**
 * Unified edge processing utility that works for both the original vis.js and new React Flow implementations
 */

/**
 * Process edges to handle multiple connections between the same nodes
 * @param {Array} edges - Array of edges to process
 * @param {Object} options - Configuration options
 * @returns {Array} Processed edges with curve settings
 */
export function processEdges(edges, options = {}) {
  if (!edges || !edges.length) return [];

  const {
    baseCurveStrength = 0.1, // Base curve strength (original uses 0.1, new uses 0.25)
    isReactFlow = false // Whether we're processing for React Flow or the original implementation
  } = options;

  // First, normalize edges to have consistent source/target properties for internal processing
  const normalizedEdges = edges.map(edge => {
    // Create a normalized copy for processing
    const normalizedEdge = { ...edge };
    // If it's a vis.js edge (with from/to)
    if (!isReactFlow) {
      normalizedEdge.source = edge.from;
      normalizedEdge.target = edge.to;
    }
    return normalizedEdge;
  });

  // Group edges by their node pairs
  const edgeGroups = {};

  normalizedEdges.forEach(edge => {
    // Now we can safely use the normalized properties
    const sourceId = String(edge.source);
    const targetId = String(edge.target);

    // Create a bidirectional key (always sorted to ensure consistency)
    const key = sourceId < targetId ? `${sourceId}-${targetId}` : `${targetId}-${sourceId}`;

    // Track original direction
    const isSourceIdGreaterTargetId = sourceId > targetId;

    if (!edgeGroups[key]) {
      edgeGroups[key] = [];
    }

    edgeGroups[key].push({
      ...edge,
      isSourceIdGreaterTargetId
    });
  });

  // Process each group of edges
  return Object.values(edgeGroups)
    .map(group => {
      const totalEdges = group.length;
      const hasOddNumberOfEdges = totalEdges % 2 !== 0;

      return group.map((edge, index) => {
        // Original edge before any modifications
        const originalEdge = { ...edge };

        // Determine if this edge should be curved
        const isStraightEdge = index === 0 && hasOddNumberOfEdges;

        // the curve strength increases every 2 edges (in the odd case, the first edge is straight)
        const pairIndex = Math.floor((index - (hasOddNumberOfEdges ? 1 : 0)) / 2);
        const curveStrength = baseCurveStrength + pairIndex * baseCurveStrength;

        // we alternate the curve direction
        const isEvenIndex = index % 2 === 0;

        // For React Flow output format
        if (isReactFlow) {
          if (isStraightEdge) {
            return {
              ...originalEdge,
              data: {
                ...(originalEdge.data || {}),
                curve: { enabled: false },
                edgeIndex: index,
                totalEdges
              }
            };
          } else {
            const normalDirection = isEvenIndex ? 'clockwise' : 'counterClockwise';

            // If it's a backward edge, use the opposite curve direction
            const curveDirection = edge.isSourceIdGreaterTargetId
              ? normalDirection === 'clockwise'
                ? 'counterClockwise'
                : 'clockwise'
              : normalDirection;

            return {
              ...originalEdge,
              data: {
                ...(originalEdge.data || {}),
                curve: {
                  enabled: true,
                  direction: curveDirection,
                  strength: curveStrength
                },
                edgeIndex: index,
                totalEdges
              }
            };
          }
        }
        // For vis.js output format
        else {
          const commonEdgeProperties = {
            ...originalEdge,
            arrows: { to: { enabled: originalEdge.shouldShowEdgeName, type: 'triangle', scaleFactor: 1 } },
            arrowStrikethrough: true,
            font: { align: 'top', strokeWidth: 0 },
            endPointOffset: {
              from: 50,
              to: 50
            }
          };

          if (isStraightEdge) {
            return {
              ...commonEdgeProperties,
              smooth: { enabled: false }
            };
          } else {
            let edgeType = isEvenIndex ? 'curvedCW' : 'curvedCCW';

            // Need to flip edge type if going backward
            if (edge.isSourceIdGreaterTargetId) {
              edgeType = edgeType === 'curvedCW' ? 'curvedCCW' : 'curvedCW';
            }

            return {
              ...commonEdgeProperties,
              smooth: {
                enabled: true,
                type: edgeType,
                roundness: curveStrength
              }
            };
          }
        }
      });
    })
    .flat();
}

/**
 * Calculate edge path parameters for curved edges in React Flow
 * @param {Object} params - Edge parameters with sourceX, sourceY, targetX, targetY
 * @param {Object} curve - Curve settings from edge data
 * @returns {Array} [path, labelX, labelY]
 */
export function calculateEdgePath(params, curve = {}) {
  const { sourceX, sourceY, targetX, targetY, isSelfEdge } = params;

  // For self-edges, create an arc above the node
  if (isSelfEdge) {
    // Calculate the distance between the source and target points
    // (which are positioned on the top of the node)
    const distance = Math.abs(targetX - sourceX);

    // Size of the loop - proportional to the node width
    const loopHeight = distance * 0.8;

    // Calculate control points for the cubic Bezier curve
    // These create a rounded arc above the node
    const controlPoint1X = sourceX;
    const controlPoint1Y = sourceY - loopHeight;

    const controlPoint2X = targetX;
    const controlPoint2Y = sourceY - loopHeight;

    // Create the cubic Bezier path
    const path = `M ${sourceX} ${sourceY} 
                  C ${controlPoint1X} ${controlPoint1Y}, 
                    ${controlPoint2X} ${controlPoint2Y}, 
                    ${targetX} ${targetY}`;

    // Position the label at the top of the arc
    const labelX = (sourceX + targetX) / 2;
    const labelY = sourceY - loopHeight;

    return [path, labelX, labelY];
  }

  // For straight edges
  if (!curve || !curve.enabled) {
    const centerX = (sourceX + targetX) / 2;
    const centerY = (sourceY + targetY) / 2;
    return [`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`, centerX, centerY];
  }

  // Calculate the midpoint between the two nodes
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  // Calculate the direction vector from source to target
  const directionX = targetX - sourceX;
  const directionY = targetY - sourceY;

  // Calculate the perpendicular vector (rotate 90 degrees)
  // For consistent curvature, we use perpendicular vectors
  const length = Math.sqrt(directionX * directionX + directionY * directionY);

  // Guard against division by zero
  if (length < 0.001) {
    return [`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`, midX, midY];
  }

  // Create normalized perpendicular vector
  const perpX = -directionY / length;
  const perpY = directionX / length;

  // Scale perpendicular vector by desired curvature and distance
  // Use a fixed percentage of the length for consistent curvature
  const curveStrength = curve.strength || 0.25;
  const fixedCurvature = length * curveStrength;

  // Apply curve direction
  const curveFactor = curve.direction === 'counterClockwise' ? -fixedCurvature : fixedCurvature;

  // Control point is in the direction of the perpendicular vector
  const controlX = midX + perpX * curveFactor;
  const controlY = midY + perpY * curveFactor;

  // Generate quadratic bezier curve
  const path = `M ${sourceX} ${sourceY} Q ${controlX} ${controlY} ${targetX} ${targetY}`;

  return [path, controlX, controlY];
}

/**
 * Prepares edges for rendering by adding edge parameters
 * @param {Array} edges - Processed edges
 * @param {Function} getNode - Function to get a node by id
 * @returns {Array} Edges with edge parameters
 */
export function computeEdgeStartAndEndPosition(edges, getNode) {
  if (!edges || !edges.length || !getNode) return [];

  return edges.map(edge => {
    const sourceNode = getNode(edge.source);
    const targetNode = getNode(edge.target);
    const edgeParams = getEdgeParams(sourceNode, targetNode);

    return {
      ...edge,
      data: {
        ...edge.data,
        edgeParams
      }
    };
  });
}

/**
 * Gets edge parameters for drawing edges between nodes
 * @param {Object} sourceNode - Source node
 * @param {Object} targetNode - Target node
 * @returns {Object} Edge parameters with isSelfEdge flag
 */
export function getEdgeParams(sourceNode, targetNode) {
  // Default values if nodes aren't available
  const defaultParams = {
    sourceX: 0,
    sourceY: 0,
    targetX: 0,
    targetY: 0,
    isSelfEdge: false
  };

  // Defensive check - if either node is missing or positions are missing
  if (!sourceNode || !targetNode || !sourceNode.position || !targetNode.position) {
    return defaultParams;
  }

  // Check if this is a self-edge (same node)
  const isSelfEdge = sourceNode.id === targetNode.id;

  // Get node positions and dimensions
  const sPos = sourceNode.position;
  const tPos = targetNode.position;
  const sWidth = sourceNode.measured?.width ?? 100;
  const sHeight = sourceNode.measured?.height ?? 40;
  const tWidth = targetNode.measured?.width ?? 100;
  const tHeight = targetNode.measured?.height ?? 40;

  // Calculate centers
  const sourceCenterX = sPos.x + sWidth / 2;
  const sourceCenterY = sPos.y + sHeight / 2;
  const targetCenterX = tPos.x + tWidth / 2;
  const targetCenterY = tPos.y + tHeight / 2;

  // Handle self-edge case
  if (isSelfEdge) {
    // For self-edges, we'll define custom source and target points
    // By convention, place the source at the top-center and target at the top-right
    // This creates a nice clockwise loop above the node

    // Calculate a slight offset from the top-center and top-right
    // to ensure the points are on the node boundary
    const topCenterX = sourceCenterX;
    const topY = sPos.y;
    const topRightX = sPos.x + sWidth * 0.75; // Slightly left of the true right edge

    return {
      sourceX: topCenterX,
      sourceY: topY,
      targetX: topRightX,
      targetY: topY,
      isSelfEdge: true
    };
  }

  // For normal edges, continue with the existing logic
  // Direction vector from source-center to target-center
  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;

  // Calculate intersection points with node boundaries
  const sourceIntersection = calculateIntersection(
    sourceCenterX,
    sourceCenterY,
    dx,
    dy,
    sPos.x,
    sPos.x + sWidth,
    sPos.y,
    sPos.y + sHeight
  );

  const targetIntersection = calculateIntersection(
    targetCenterX,
    targetCenterY,
    -dx,
    -dy,
    tPos.x,
    tPos.x + tWidth,
    tPos.y,
    tPos.y + tHeight
  );

  return {
    sourceX: sourceIntersection.x,
    sourceY: sourceIntersection.y,
    targetX: targetIntersection.x,
    targetY: targetIntersection.y,
    isSelfEdge: false
  };
}

/**
 * Calculates intersection point of a line with a rectangle
 * @param {number} cx - Center X
 * @param {number} cy - Center Y
 * @param {number} dx - Direction X
 * @param {number} dy - Direction Y
 * @param {number} left - Rectangle left
 * @param {number} right - Rectangle right
 * @param {number} top - Rectangle top
 * @param {number} bottom - Rectangle bottom
 * @returns {Object} Intersection point {x, y}
 */
function calculateIntersection(cx, cy, dx, dy, left, right, top, bottom) {
  const EPSILON = 1e-6;

  // If direction is near zero, return center to avoid division by zero
  if (Math.abs(dx) < EPSILON && Math.abs(dy) < EPSILON) {
    return { x: cx, y: cy };
  }

  const candidates = [];

  // Check intersections with vertical boundaries (left/right)
  if (Math.abs(dx) > EPSILON) {
    // Left boundary
    const tLeft = (left - cx) / dx;
    if (tLeft >= 0) {
      const yAtLeft = cy + tLeft * dy;
      if (yAtLeft >= top && yAtLeft <= bottom) {
        candidates.push({ t: tLeft, x: left, y: yAtLeft });
      }
    }

    // Right boundary
    const tRight = (right - cx) / dx;
    if (tRight >= 0) {
      const yAtRight = cy + tRight * dy;
      if (yAtRight >= top && yAtRight <= bottom) {
        candidates.push({ t: tRight, x: right, y: yAtRight });
      }
    }
  }

  // Check intersections with horizontal boundaries (top/bottom)
  if (Math.abs(dy) > EPSILON) {
    // Top boundary
    const tTop = (top - cy) / dy;
    if (tTop >= 0) {
      const xAtTop = cx + tTop * dx;
      if (xAtTop >= left && xAtTop <= right) {
        candidates.push({ t: tTop, x: xAtTop, y: top });
      }
    }

    // Bottom boundary
    const tBottom = (bottom - cy) / dy;
    if (tBottom >= 0) {
      const xAtBottom = cx + tBottom * dx;
      if (xAtBottom >= left && xAtBottom <= right) {
        candidates.push({ t: tBottom, x: xAtBottom, y: bottom });
      }
    }
  }

  if (!candidates.length) {
    // If no valid intersection found, fallback to the center
    return { x: cx, y: cy };
  }

  // Pick the smallest positive t (closest intersection)
  return candidates.reduce((closest, current) => (current.t < closest.t ? current : closest));
}
